import { Chart as ChartJS } from "chart.js";
import { buildChartConfig } from "../components/ChartWidget";

// Paints the canvas white before the chart draws: a PNG from Chart.js is otherwise transparent, which
// a PDF shows as black in some viewers.
const whiteBackground = {
  id: "reportWhiteBackground",
  beforeDraw(chart) {
    const { ctx, width, height } = chart;
    ctx.save();
    ctx.globalCompositeOperation = "destination-over";
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, width, height);
    ctx.restore();
  },
};

/**
 * One chart spec drawn off-screen to a PNG data URL, with exactly the config the dashboard renders it
 * with (ChartWidget.buildChartConfig), so the downloaded report shows the same chart as the screen.
 * Off-screen rather than captured from the page: a card on its Table or Narration tab has no canvas.
 */
export function chartToPng(spec, { width = 1400, height = 640 } = {}) {
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const cfg = buildChartConfig(spec);
  const chart = new ChartJS(canvas.getContext("2d"), {
    ...cfg,
    options: {
      ...cfg.options,
      responsive: false,
      animation: false,
      devicePixelRatio: 1,
      plugins: {
        ...cfg.options.plugins,
        title: { ...cfg.options.plugins.title, font: { size: 22, weight: "600" } },
        legend: { ...cfg.options.plugins.legend, labels: { boxWidth: 18, font: { size: 16 } } },
      },
      scales: Object.fromEntries(
        Object.entries(cfg.options.scales || {}).map(([k, v]) => [k, { ...v, ticks: { ...v.ticks, font: { size: 15 } } }])
      ),
    },
    plugins: [whiteBackground],
  });
  try {
    return canvas.toDataURL("image/png");
  } finally {
    chart.destroy();
  }
}

/** {widgetId: png} for every chart widget on a dashboard. A chart that fails to draw is left out, and
 *  the report prints its numbers as a table instead. */
export function dashboardChartImages(widgets) {
  const out = {};
  for (const w of widgets || []) {
    if (w.kind !== "chart" || !w.spec) continue;
    try {
      out[w.id] = chartToPng(w.spec);
    } catch {
      // leave it to the server's table fallback
    }
  }
  return out;
}
